import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, MenuItem, Select, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';

import { COLORS } from '../IndividualPlan/tokens';
import { parseCsvRecords } from '../Upload/parseCsvRow';
import {
  SIS_FIELDS,
  applySavedSisMapping,
  proposeSisMapping,
  readSisColumns,
  validateSisMapping,
  valueShapeHint,
  type SisColumn,
  type SisField,
  type SisFieldTier,
  type SisMapping,
  type SisMappingWarning, absentMappingValue } from '../Upload/sisFieldMapping';
import type { UploadKind } from './uploadedFiles';

// A select value of its own, so choosing the option from the unset state fires a change.
const NOT_IN_FILE_VALUE = 'not-in-file';

const INK = '#08081B';
const MUTED = '#63637D';
const BORDER = 'rgba(8, 8, 27, 0.15)';
const PANEL_BG = '#F6F6F8';
const CALLOUT_BG = '#CCE8FA';
const CALLOUT_FG = '#072254';

const SHAPE_HINT_LABELS: Record<'dates' | 'ids', string> = {
  dates: 'looks like dates',
  ids: 'looks like IDs',
};

export function warningMessage(warning: SisMappingWarning): string {
  const field = SIS_FIELDS.find((f) => f.key === warning.field);
  const label = field?.label ?? warning.field;
  if (warning.code === 'mostly_blank') {
    const pct = Math.round(warning.blankShare * 100);
    const impact = field?.blankImpact;
    const tail = impact ? ` — ${impact}` : '.';
    return `${label} is mapped, but ${pct}% of rows are blank${tail}`;
  }
  if (warning.code === 'no_recognized_values') {
    return `None of the values in the column mapped to ${label} are recognized exit reasons. These departures will all be grouped under "Other" — none will count as reclassifications, so the exit rate may be understated. Recognized values include "Reclassified", "RFEP", "Met Exit Criteria", "Moved", and "Transferred".`;
  }
  const rows = warning.affectedRows === 1 ? '1 value' : `${warning.affectedRows} values`;
  return `${rows} in the column mapped to ${label} couldn't be read. Affected rows are skipped for this field.`;
}

export function FieldRow({
  field,
  columns,
  mappedIndex,
  onChange,
  disabled,
}: {
  field: SisField;
  columns: SisColumn[];
  mappedIndex: number | undefined;
  onChange: (index: number | undefined) => void;
  disabled?: boolean;
}) {
  const sample = mappedIndex === undefined || mappedIndex < 0 ? undefined : columns[mappedIndex]?.samples[0];
  return (
    <Box sx={{ py: 1.5, borderBottom: `1px solid ${BORDER}`, '&:last-child': { borderBottom: 'none' } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14, color: INK }}>{field.label}</Typography>
          <Typography sx={{ fontSize: 13, color: MUTED }}>{field.consequence}</Typography>
        </Box>
        <Box sx={{ width: { xs: '100%', sm: 260 }, flexShrink: 0 }}>
          <Select
            size="small"
            fullWidth
            displayEmpty
            disabled={disabled}
            value={mappedIndex === undefined ? '' : mappedIndex < 0 ? NOT_IN_FILE_VALUE : String(mappedIndex)}
            onChange={(e) => onChange(e.target.value === NOT_IN_FILE_VALUE || e.target.value === '' ? undefined : Number(e.target.value))}
            SelectDisplayProps={{ 'aria-label': `Column for ${field.label}` }}
            renderValue={(value) =>
              value === NOT_IN_FILE_VALUE ? (
                'Not in my file'
              ) : value === '' ? (
                <Typography component="span" sx={{ fontSize: 13, color: MUTED }}>
                  Select column
                </Typography>
              ) : (
                columns[Number(value)]?.header
              )
            }
            sx={{ fontSize: 13, bgcolor: PANEL_BG }}
          >
            <MenuItem value={NOT_IN_FILE_VALUE}>Not in my file</MenuItem>
            {columns.map((col) => {
              const hint = valueShapeHint(col.samples);
              return (
                <MenuItem key={col.index} value={String(col.index)} sx={{ fontSize: 13 }}>
                  {col.header}
                  {hint && (
                    <Typography component="span" sx={{ ml: 1, fontSize: 12, color: MUTED }}>
                      {SHAPE_HINT_LABELS[hint]}
                    </Typography>
                  )}
                </MenuItem>
              );
            })}
          </Select>
          {sample && (
            <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.5 }}>e.g. {sample}</Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
}

const ROSTER_PILL = 'Student Roster (SIS)';

const FIELD_GROUPS: { title: string; description: string; tiers: SisFieldTier[] }[] = [
  {
    title: 'Strongly Recommended',
    description:
      'The following columns are used the most in data analysis. If these columns aren’t present, some aspects of the plans and reports may be unavailable.',
    tiers: ['load_bearing'],
  },
  {
    title: 'Additional Helpful Columns',
    description:
      'The following columns are helpful to provide a complete picture of a student and help with cohort analysis.',
    tiers: ['helpful', 'optional'],
  },
];

export function seedMapping(columns: SisColumn[]): SisMapping {
  const saved = applySavedSisMapping(columns);
  return saved.kind === 'partial' ? saved.mapping : proposeSisMapping(columns);
}

type MatchRosterFieldsProps = {
  name: string;
  kind?: UploadKind;
  csv: string;
  mapping: SisMapping;
  onMappingChange: (mapping: SisMapping) => void;
  disabled?: boolean;
};

export function MatchRosterFields({
  name,
  kind,
  csv,
  mapping,
  onMappingChange,
  disabled,
}: MatchRosterFieldsProps) {
  const columns = useMemo(() => (csv ? readSisColumns(csv) : []), [csv]);
  const counts = useMemo(() => {
    if (!csv) return { rows: 0, columns: 0 };
    const records = parseCsvRecords(csv);
    return {
      rows: Math.max(records.length - 1, 0),
      columns: records[0]?.length ?? 0,
    };
  }, [csv]);
  const warnings = useMemo(
    () => (csv ? validateSisMapping(csv, mapping) : []),
    [csv, mapping],
  );

  const setField = (field: SisField, index: number | undefined) => {
    const next = { ...mapping };
    const absent = absentMappingValue(field.key);
    if (index !== undefined) {
      next[field.key] = index;
    } else if (absent !== undefined) {
      next[field.key] = absent;
    } else {
      delete next[field.key];
    }
    onMappingChange(next);
  };

  return (
    <>
      <Box
        sx={{
          mt: 2,
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          bgcolor: COLORS.bg,
          borderRadius: '8px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionOutlinedIcon sx={{ fontSize: 24, color: COLORS.muted }} />
          <Box>
            <Typography sx={{ fontSize: 16, color: COLORS.ink }}>{name}</Typography>
            <Typography sx={{ fontSize: 12, color: COLORS.muted }}>
              {`${counts.rows.toLocaleString('en-US')} rows · ${counts.columns} columns detected`}
            </Typography>
          </Box>
        </Box>
        {kind === 'student_roster' && (
          <Box
            sx={{
              padding: '4px 12px',
              bgcolor: CALLOUT_BG,
              borderRadius: '9999px',
              whiteSpace: 'nowrap',
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: CALLOUT_FG }}>
              {ROSTER_PILL}
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          mt: 2,
          padding: '16px',
          display: 'flex',
          gap: 1,
          bgcolor: CALLOUT_BG,
          borderRadius: '8px',
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 24, color: CALLOUT_FG }} />
        <Typography sx={{ fontSize: 16, color: CALLOUT_FG }}>
          Nothing here blocks your reports. Your WIDA ACCESS scores already generate plans
          and evaluations; each roster field you match just adds more detail. Leave any
          field unmatched and we&rsquo;ll tell you what it affects.
        </Typography>
      </Box>

      {FIELD_GROUPS.map((group) => (
        <Box key={group.title} sx={{ mt: 4 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: COLORS.ink }}>
            {group.title}
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 14, color: COLORS.muted }}>
            {group.description}
          </Typography>
          <Box sx={{ mt: 1 }}>
            {SIS_FIELDS.filter((f) => group.tiers.includes(f.tier)).map((field) => (
              <FieldRow
                key={field.key}
                field={field}
                columns={columns}
                mappedIndex={mapping[field.key]}
                onChange={(index) => setField(field, index)}
                disabled={disabled}
              />
            ))}
          </Box>
        </Box>
      ))}

      {warnings.length > 0 && (
        <Box
          role="status"
          sx={{
            mt: 2.5,
            px: 2,
            py: 1.5,
            borderRadius: '8px',
            bgcolor: '#FFF4DB',
            color: '#6A4A00',
          }}
        >
          {warnings.map((warning) => (
            <Typography key={`${warning.code}-${warning.field}`} sx={{ fontSize: 13, py: 0.25 }}>
              {warningMessage(warning)}
            </Typography>
          ))}
        </Box>
      )}
    </>
  );
}
